import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Segment } from '@feathr/blackbox';
import type { IToastAction } from '@feathr/components';
import { useExportModal } from '@feathr/extender/hooks/useExportModal';

import type { IUseEmailActivityTabReturn } from './EmailActivityTab/EmailActivityTab.hooks';
import { useEmailActivityTab } from './EmailActivityTab/EmailActivityTab.hooks';
import type {
  IGroupExportTabHook,
  ISharedFormSlice,
  TGroupExportTab,
} from './GroupExportModal.types';
import type { IEmailActivityExportPayload } from './GroupExportModal.utils';
import { resolveExportNameTemplate } from './GroupExportModal.utils';
import type { IUsePersonTabReturn } from './PersonTab/PersonTab.hooks';
import { usePersonTab } from './PersonTab/PersonTab.hooks';

export type { TGroupExportTab } from './GroupExportModal.types';

const EMAIL_ACTIVITY_PRESET_IDS = ['30d', '60d', '90d'];
const EMAIL_ACTIVITY_DEFAULT_DATE_RANGE = '30d';

export interface IUseGroupExportModalOptions {
  segment: Segment;
  onClose: () => void;
  onExportEmailActivity?: (payload: IEmailActivityExportPayload) => Promise<void>;
  successToastAction?: IToastAction;
}

export interface IUseGroupExportModalReturn {
  activeTab: TGroupExportTab;
  setActiveTab: (tab: TGroupExportTab) => void;
  isLoading: boolean;
  isSubmitDisabled: boolean;
  personTab: IUsePersonTabReturn;
  emailActivityTab: IUseEmailActivityTabReturn;
  handleSubmit: () => Promise<void>;
  handleClose: () => void;
}

export function useGroupExportModal({
  segment,
  onClose,
  onExportEmailActivity,
  successToastAction,
}: IUseGroupExportModalOptions): IUseGroupExportModalReturn {
  const { t } = useTranslation();

  // Reactive read so MobX re-renders once the segment's name loads.
  const resolvedExportName = resolveExportNameTemplate(segment.get('name', ''));

  const sharedExport = useExportModal(t, {
    initialExportName: resolvedExportName,
    presetIds: EMAIL_ACTIVITY_PRESET_IDS,
    defaultDateRange: EMAIL_ACTIVITY_DEFAULT_DATE_RANGE,
  });

  const [activeTab, setActiveTab] = useState<TGroupExportTab>('person');

  const sharedFormSlice: ISharedFormSlice = {
    exportName: sharedExport.exportName,
    exportNameHadFocus: sharedExport.exportNameHadFocus,
    exportNameError: sharedExport.validationErrors?.exportName,
    handleChangeExportName: sharedExport.handleChangeExportName,
    handleExportNameBlur: sharedExport.handleExportNameBlur,
    email: sharedExport.email,
    emailHadFocus: sharedExport.emailHadFocus,
    emailError: sharedExport.validationErrors?.email,
    handleChangeEmail: sharedExport.handleChangeEmail,
    handleEmailBlur: sharedExport.handleEmailBlur,
  };

  const personTab = usePersonTab({
    segment,
    form: sharedFormSlice,
    handleExport: sharedExport.handleExport,
  });

  const emailActivityTab = useEmailActivityTab({
    form: sharedFormSlice,
    dateRange: {
      selectedDateRange: sharedExport.selectedDateRange,
      customDateRange: sharedExport.customDateRange,
      dateRangeHadFocus: sharedExport.dateRangeHadFocus,
      dateRangeOptions: sharedExport.dateRangeOptions,
      dateRangeError: sharedExport.validationErrors?.dateRange,
      handleDateRangeChange: sharedExport.handleDateRangeChange,
      handleCustomDateRangeChange: sharedExport.handleCustomDateRangeChange,
    },
    resolveDateRange: sharedExport.resolveDateRange,
    handleExport: sharedExport.handleExport,
    onExportEmailActivity,
    successToastAction,
  });

  const handleClose = useCallback((): void => {
    sharedExport.resetFormState();
    personTab.resetState();
    emailActivityTab.resetState();
    setActiveTab('person');
    onClose();
  }, [emailActivityTab.resetState, onClose, personTab.resetState, sharedExport.resetFormState]);

  // Record lookup so adding a new TGroupExportTab id raises a TS error here.
  const tabStatesByTab: Record<TGroupExportTab, IGroupExportTabHook<unknown> | undefined> = {
    person: personTab,
    emailActivity: emailActivityTab,
    otherActivities: undefined,
  };
  const activeTabState = tabStatesByTab[activeTab];

  const isEmailActivityHandlerMissing =
    activeTab === 'emailActivity' && onExportEmailActivity === undefined;

  const isSubmitDisabled =
    sharedExport.isLoading ||
    activeTabState === undefined ||
    isEmailActivityHandlerMissing ||
    activeTabState.validationErrors.length > 0;

  const handleSubmit = useCallback(async (): Promise<void> => {
    const didSucceed = (await activeTabState?.handleSubmit()) ?? false;
    if (didSucceed) {
      handleClose();
    }
  }, [activeTabState, handleClose]);

  return {
    activeTab,
    setActiveTab,
    isLoading: sharedExport.isLoading,
    isSubmitDisabled,
    personTab,
    emailActivityTab,
    handleSubmit,
    handleClose,
  };
}
